import { useEffect, useState } from "react";
import { completeLogin } from "@/auth/userManager";
import { ErrorNote, linkClass } from "@/components/ui";
import { useT } from "@/i18n/LocaleProvider";

/**
 * Handles the redirect back from the identity provider.
 *
 * Exchanges the authorization code for tokens, then replaces the URL so the code
 * does not linger in history or in a copied link.
 */
export function CallbackPage() {
  // This page renders outside the Layout and before /me has answered, so its
  // language is the browser's — the same one the sign-in screen used, which is
  // where the person just came from.
  const t = useT();
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    completeLogin()
      .then(() => {
        if (cancelled) return;
        window.history.replaceState({}, document.title, "/");
        window.location.replace("/");
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-md py-16">
        <h1 className="text-lg font-semibold text-ink">{t("callback.failed")}</h1>
        <ErrorNote error={error} />
        <a href="/" className={`mt-4 inline-block text-sm ${linkClass}`}>
          {t("callback.backToSignIn")}
        </a>
      </div>
    );
  }

  return (
    <p className="flex items-center justify-center gap-2 py-16 text-sm text-muted" role="status">
      <span
        aria-hidden="true"
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line border-t-accent"
      />
      {t("callback.completing")}
    </p>
  );
}
